import React, { useEffect, useRef, useState } from 'react';
import { ReloadOutlined, PauseOutlined, CaretDownOutlined } from '@ant-design/icons';
import { LogEntry } from '../../../types/wordpress.type';
import EventItem from './EventItem';

interface EventStreamProps {
  events: LogEntry[];
}

const EventStream: React.FC<EventStreamProps> = ({ events }) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState<'ALL' | 'INFO' | 'WARN' | 'ERROR' | 'SUCCESS'>('ALL');
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevEventsLength = useRef(events.length);

  useEffect(() => {
    if (autoScroll && scrollRef.current && events.length > prevEventsLength.current) {
      scrollRef.current.scrollTop = 0;
    }
    prevEventsLength.current = events.length;
  }, [events, autoScroll]);

  const filteredEvents = events.filter((event) => {
    if (filter === 'ALL') return true;
    return event.severity === filter;
  });

  const severityCounts = {
    ALL: events.length,
    INFO: events.filter((e) => e.severity === 'INFO').length,
    WARN: events.filter((e) => e.severity === 'WARN').length,
    ERROR: events.filter((e) => e.severity === 'ERROR').length,
    SUCCESS: events.filter((e) => e.severity === 'SUCCESS').length,
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden sticky top-6 h-[calc(100vh-200px)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Events</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{events.length} total events</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoScroll(!autoScroll)}
              className={`p-1.5 rounded transition-colors text-sm ${
                autoScroll
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={autoScroll ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}
            >
              {autoScroll ? <CaretDownOutlined /> : <PauseOutlined />}
            </button>
            <button
              onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              className="p-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
              title="Scroll to latest"
            >
              <ReloadOutlined />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-1.5 flex-wrap">
          {(['ALL', 'SUCCESS', 'INFO', 'WARN', 'ERROR'] as const).map((severity) => (
            <button
              key={severity}
              onClick={() => setFilter(severity)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                filter === severity
                  ? severity === 'ALL'
                    ? 'bg-dark-bg text-white'
                    : severity === 'SUCCESS'
                    ? 'bg-green-600 text-white'
                    : severity === 'INFO'
                    ? 'bg-blue-600 text-white'
                    : severity === 'WARN'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {severity} <span className="opacity-75">({severityCounts[severity]})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Events List */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4"
      >
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-sm">No events to display</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredEvents.map((event) => (
              <EventItem key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          {autoScroll ? '🔵 Auto-scrolling enabled' : '⏸️ Auto-scroll paused'}
        </p>
      </div>
    </div>
  );
};

export default EventStream;